import { jsxs, jsx } from 'react/jsx-runtime';
import React, { forwardRef, useId, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid, parse } from 'date-fns';
import { useValidateFormRequiredProps, dsI18n, getCommonClassNameDefault, getCommonAutoCompleteDefault, getCommonFormVariantDefault } from '@skatteetaten/ds-core-utils';
import { CalendarIcon } from '@skatteetaten/ds-icons';
import { DatePickerCalendar } from '../DatePickerCalendar/index.esm.js';
import { ErrorMessage } from '../ErrorMessage/index.esm.js';
import { LabelWithHelp } from '../LabelWithHelp/index.esm.js';
import './styles.css';

import '@skatteetaten/ds-buttons';
import '../Select/index.esm.js';
import '../SelectOption/index.esm.js';
import '../TextField/index.esm.js';
import '../Help/index.esm.js';
import './styles.css';


const getDatePickerDateFormat = ()=>'dd.MM.yyyy';

const formatDateForInput = (dateFormat, date)=>{
    return !isValid(date) ? '' : format(date, dateFormat);
};
const COMMON_INPUT_FORMATS = [
    'dd.MM.yy',
    'dd/MM/yy',
    'dd-MM-yy',
    'dd.MM.yyyy',
    'dd/MM/yyyy',
    'dd-MM-yyyy',
    'ddMM',
    'ddMMyy',
    'ddMMyyyy'
];
const parseDateFromInput = (value, dateFormat)=>{
    const dateFormats = Array.from(new Set([
        ...COMMON_INPUT_FORMATS,
        dateFormat
    ]));
    for (const format of dateFormats){
        const date = parse(value, format, new Date());
        if (isValid(date)) {
            return date;
        }
    }
    return null;
};

var styles = {
    "dateContainer": "DatePicker-module_dateContainer__b6xbs",
    "input": "DatePicker-module_input__7whov",
    "calendarButton": "DatePicker-module_calendarButton__8q9cL",
    "input_large": "DatePicker-module_input_large__AJfBa",
    "icon": "DatePicker-module_icon__CgJBx",
    "calendarButton_large": "DatePicker-module_calendarButton_large__UyTU2",
    "calendarContainer": "DatePicker-module_calendarContainer__1Z-HE"
};

const DatePicker = /*#__PURE__*/ forwardRef(({ id: externalId, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, dateFormat = getDatePickerDateFormat(), disabledDates, description, errorMessage, helpSvgPath, helpText, label, initialPickerDate, minDate, maxDate, titleHelpSvg, value, variant = getCommonFormVariantDefault(), autoComplete = getCommonAutoCompleteDefault(), disabled, name, placeholder, readOnly, required, hideLabel, showRequiredMark, onBlur, onChange, onFocus, onHelpToggle, onSelectDate }, ref)=>{
    useValidateFormRequiredProps({
        required,
        showRequiredMark
    });
    const { t } = useTranslation('ds_forms', {
        i18n: dsI18n
    });
    const errorId = `datepickerErrorId-${useId()}`;
    const generatedId = `datepickerInputId-${useId()}`;
    const datePickerId = externalId ?? generatedId;
    const calendarRef = useRef(null);
    const calenderButtonRef = useRef(null);
    const inputRef = useRef(null);
    useImperativeHandle(ref, ()=>inputRef?.current);
    const [showCalendar, setShowCalendar] = useState(false);
    const [inputValue, setInputValue] = React.useState(formatDateForInput(dateFormat, value));
    useEffect(()=>{
        const currentTextInput = inputRef.current?.value;
        const isCurrentInputValidDate = isValid(parseDateFromInput(currentTextInput ?? '', dateFormat));
        /**
       * Oppdaterer verdien i inputfeltet hvis én av følgende betingelser er oppfylt:
       * 1. Eksisterende tekstverdi er en gyldig dato
       * 2. Inputfeltet er tomt
       * 3. En gyldig dato sendes inn via 'value'-prop
       */ const shouldUpdateInputValue = isCurrentInputValidDate || currentTextInput === '' || !!value;
        if (shouldUpdateInputValue) {
            setInputValue(formatDateForInput(dateFormat, value));
        }
    }, [
        dateFormat,
        value
    ]);
    const parsedDateFromInput = parseDateFromInput(inputValue, dateFormat) ?? undefined;
    const preselectedDate = isValid(parsedDateFromInput) ? parsedDateFromInput : initialPickerDate;
    const handleChange = (e)=>{
        setInputValue(e.target.value);
        onChange?.(e);
    };
    const handleFocus = (e)=>{
        if (showCalendar) {
            setShowCalendar(false);
        }
        onFocus?.(e);
    };
    const handleBlur = (e)=>{
        const { value } = e.target;
        const date = parseDateFromInput(value, dateFormat);
        if (isValid(date)) {
            date && setInputValue(formatDateForInput(dateFormat, date));
        }
        onSelectDate?.(date);
        onBlur?.(e);
    };
    const handleSelectDate = (date)=>{
        setInputValue(formatDateForInput(dateFormat, date));
        setShowCalendar(false);
        inputRef.current?.focus();
        onSelectDate?.(date);
    };
    const closeCalendar = ()=>{
        setShowCalendar(false);
        calenderButtonRef?.current?.focus();
    };
    useEffect(()=>{
        if (!showCalendar) {
            return;
        }
        const handleOutside = (event)=>{
            const composedPath = event.composedPath();
            const node = composedPath.length > 0 ? composedPath[0] : event.target;
            if (!calendarRef?.current?.contains(node) && !calenderButtonRef?.current?.contains(node)) {
                setShowCalendar(false);
                event.type === 'click' && calenderButtonRef?.current?.focus();
            }
        };
        let previousWidth = window.innerWidth;
        const handleResize = (e)=>{
            const newWidth = window.innerWidth;
            if (e.type === 'resize' && newWidth < previousWidth) {
                closeCalendar();
            }
            previousWidth = newWidth;
        };
        const handleEscape = (e)=>{
            if (e.key === 'Escape') {
                closeCalendar();
            }
        };
        document.addEventListener('click', handleOutside);
        window.addEventListener('resize', handleResize);
        document.addEventListener('keydown', handleEscape);
        return ()=>{
            document.removeEventListener('click', handleOutside);
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [
        showCalendar
    ]);
    const placeholderValue = placeholder?.trim() === '' ? undefined : placeholder ?? t('datepicker.TypeOrSelect');
    const isLarge = variant === 'large';
    const inputClassName = `${styles.input} ${isLarge ? styles.input_large : ''}`.trim();
    const calendarButtonClassName = `${styles.calendarButton} ${isLarge ? styles.calendarButton_large : ''}`.trim();
    return /*#__PURE__*/ jsxs("div", {
        className: `${className} ${classNames?.container ?? ''}`.trim(),
        lang: lang,
        children: [
            /*#__PURE__*/ jsx(LabelWithHelp, {
                classNames: classNames,
                htmlFor: datePickerId,
                hideLabel: hideLabel,
                showRequiredMark: showRequiredMark,
                description: description,
                helpSvgPath: helpSvgPath,
                helpText: helpText,
                titleHelpSvg: titleHelpSvg,
                onHelpToggle: onHelpToggle,
                children: label
            }),
            /*#__PURE__*/ jsxs("div", {
                className: `${styles.dateContainer} ${classNames?.dateContainer ?? ''}`.trim(),
                children: [
                    /*#__PURE__*/ jsx("input", {
                        ref: inputRef,
                        id: datePickerId,
                        className: inputClassName,
                        "data-testid": dataTestId,
                        autoComplete: autoComplete,
                        disabled: disabled,
                        name: name,
                        placeholder: placeholderValue,
                        readOnly: readOnly,
                        required: required,
                        value: inputValue,
                        "aria-describedby": errorMessage ? errorId : undefined,
                        "aria-invalid": !!errorMessage || undefined,
                        onBlur: handleBlur,
                        onChange: handleChange,
                        onFocus: handleFocus
                    }),
                    !readOnly && /*#__PURE__*/ jsx("button", {
                        ref: calenderButtonRef,
                        type: 'button',
                        className: calendarButtonClassName,
                        disabled: disabled,
                        "aria-expanded": showCalendar,
                        onClick: ()=>setShowCalendar(!showCalendar),
                        children: /*#__PURE__*/ jsx(CalendarIcon, {
                            className: styles.icon,
                            title: t('datepicker.ChooseDate')
                        })
                    })
                ]
            }),
            /*#__PURE__*/ jsx(ErrorMessage, {
                id: errorId,
                showError: !!errorMessage,
                className: classNames?.errorMessage,
                children: errorMessage
            }),
            showCalendar && /*#__PURE__*/ jsx("div", {
                className: styles.calendarContainer,
                children: /*#__PURE__*/ jsx(DatePickerCalendar, {
                    ref: calendarRef,
                    disabledDates: disabledDates,
                    selectedDate: preselectedDate,
                    minDate: minDate,
                    maxDate: maxDate,
                    onSelectDate: handleSelectDate,
                    onTabKeyOut: closeCalendar
                })
            })
        ]
    });
});
DatePicker.displayName = 'DatePicker';

export { DatePicker, getDatePickerDateFormat };
